import { FONT_METRICS, FONT_NAMES, FontName, textWidth, toWinAnsi } from './fontMetrics';

// A small PDF 1.4 writer for the app's generated reports. It lays content out top to bottom
// with a y cursor (in points, measured from the top of the page) and starts a new page when
// the next block doesn't fit. Only the standard fonts and JPEG images are supported.

export const A4 = { width: 595.28, height: 841.89 };

export type TextStyle = {
    font?: FontName;
    size?: number;
    /** Hex colour, e.g. "#808080". Defaults to black. */
    color?: string;
    align?: 'left' | 'center' | 'right';
    underline?: boolean;
};

export type FlowTextOptions = TextStyle & {
    marginTop?: number;
    marginBottom?: number;
    /** Narrows the box the text wraps/aligns in, like a CSS padding-right. */
    paddingRight?: number;
};

/** A baseline JPEG, embedded as-is (DCTDecode). */
export type PdfImage = { bytes: Uint8Array; width: number; height: number };

export type Column = {
    /** Width in points. Columns that don't fit are shrunk proportionally (margins are kept). */
    width: number;
    marginRight?: number;
    render: () => void;
};

export type RowCell = {
    width: number;
    text: string;
    style?: TextStyle;
};

export type RowOptions = {
    background?: string;
    borderBottom?: { color: string; width: number };
    paddingX?: number;
    paddingY?: number;
};

type Margins = { top: number; right: number; bottom: number; left: number };

const DEFAULT_FONT: FontName = 'Helvetica';
const DEFAULT_SIZE = 12;

const fmt = (n: number) => String(Math.round(n * 100) / 100);

const hexToRgb = (hex: string) => {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const n = parseInt(full, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(c => fmt(c / 255)).join(' ');
};

const toHex = (codes: number[]) => codes.map(c => c.toString(16).padStart(2, '0')).join('');

/** Line height and baseline offset (from the top of the line) for a font. */
const lineMetrics = (font: FontName, size: number) => {
    const m = FONT_METRICS[font];
    return { height: ((m.ascent - m.descent) / 1000) * size, baseline: (m.ascent / 1000) * size };
};

/** Splits text into lines that fit `maxWidth`. Words longer than a line are broken by character. */
export const wrapText = (text: string, maxWidth: number, font: FontName, size: number): string[] => {
    const lines: string[] = [];
    for (const paragraph of text.split(/\r?\n/)) {
        let line = '';
        for (const word of paragraph.split(' ')) {
            const candidate = line === '' ? word : `${line} ${word}`;
            if (textWidth(candidate, font, size) <= maxWidth) {
                line = candidate;
                continue;
            }
            if (line !== '') lines.push(line);
            line = '';
            for (const char of word) {
                if (line !== '' && textWidth(line + char, font, size) > maxWidth) {
                    lines.push(line);
                    line = '';
                }
                line += char;
            }
        }
        lines.push(line);
    }
    return lines;
};

export class PdfDocument {
    readonly pageWidth = A4.width;
    readonly pageHeight = A4.height;
    readonly margins: Margins;

    /** Flow cursor, in points from the top of the current page. */
    y: number;
    /** Left edge and width of the current flow region (narrowed while rendering a column). */
    x: number;
    width: number;

    private readonly title?: string;
    private readonly pages: string[][] = [];
    private pageIndex = -1;
    private readonly images = new Map<PdfImage, string>();
    private breaksSuppressed = false;

    constructor(options: { margins: Margins; title?: string }) {
        this.margins = options.margins;
        this.title = options.title;
        this.x = this.margins.left;
        this.width = this.pageWidth - this.margins.left - this.margins.right;
        this.y = this.margins.top;
        this.addPage();
    }

    get pageCount() {
        return this.pages.length;
    }

    /** Lowest y that flow content may reach on a page. */
    get bottom() {
        return this.pageHeight - this.margins.bottom;
    }

    private get ops() {
        return this.pages[this.pageIndex];
    }

    /** Moves to the top of the next page, creating it if needed (columns can revisit earlier pages). */
    addPage() {
        if (this.pageIndex === this.pages.length - 1) this.pages.push([]);
        this.pageIndex++;
        this.y = this.margins.top;
    }

    /** Starts a new page if `height` more points won't fit, unless we're already at the top. */
    ensureSpace(height: number) {
        if (this.breaksSuppressed) return;
        if (this.y + height > this.bottom && this.y > this.margins.top + 0.01) this.addPage();
    }

    moveDown(points: number) {
        this.y += points;
    }

    // ── absolute drawing (y = top edge, in points from the top of the page) ──

    drawText(text: string, x: number, y: number, style: TextStyle = {}, boxWidth?: number) {
        const font = style.font ?? DEFAULT_FONT;
        const size = style.size ?? DEFAULT_SIZE;
        const width = textWidth(text, font, size);
        let left = x;
        if (boxWidth !== undefined && style.align === 'center') left = x + (boxWidth - width) / 2;
        if (boxWidth !== undefined && style.align === 'right') left = x + boxWidth - width;

        const baseline = this.pageHeight - (y + lineMetrics(font, size).baseline);
        const color = hexToRgb(style.color ?? '#000000');
        const fontKey = `F${FONT_NAMES.indexOf(font) + 1}`;
        this.ops.push(`BT /${fontKey} ${fmt(size)} Tf ${color} rg ${fmt(left)} ${fmt(baseline)} Td <${toHex(toWinAnsi(text))}> Tj ET`);
        if (style.underline === true && width > 0) {
            const thickness = size * 0.05;
            this.ops.push(`${color} rg ${fmt(left)} ${fmt(baseline - size * 0.1 - thickness)} ${fmt(width)} ${fmt(thickness)} re f`);
        }
    }

    drawRect(x: number, y: number, width: number, height: number, fill: string) {
        this.ops.push(`${hexToRgb(fill)} rg ${fmt(x)} ${fmt(this.pageHeight - y - height)} ${fmt(width)} ${fmt(height)} re f`);
    }

    drawImage(image: PdfImage, x: number, y: number, width: number, height = width * (image.height / image.width)) {
        let name = this.images.get(image);
        if (name === undefined) {
            name = `Im${this.images.size + 1}`;
            this.images.set(image, name);
        }
        this.ops.push(`q ${fmt(width)} 0 0 ${fmt(height)} ${fmt(x)} ${fmt(this.pageHeight - y - height)} cm /${name} Do Q`);
    }

    // ── measuring ──

    lineHeight(style: TextStyle = {}) {
        return lineMetrics(style.font ?? DEFAULT_FONT, style.size ?? DEFAULT_SIZE).height;
    }

    textHeight(text: string, width: number, style: TextStyle = {}) {
        const lines = wrapText(text, width, style.font ?? DEFAULT_FONT, style.size ?? DEFAULT_SIZE);
        return lines.length * this.lineHeight(style);
    }

    // ── flow layout ──

    /** Writes wrapped text at the cursor, breaking pages between lines as needed. null/undefined print as empty. */
    text(text: string | number | null | undefined, options: FlowTextOptions = {}) {
        const { marginTop = 0, marginBottom = 0, paddingRight = 0, ...style } = options;
        const boxWidth = this.width - paddingRight;
        const lineHeight = this.lineHeight(style);
        this.y += marginTop;
        for (const line of wrapText(text == null ? '' : String(text), boxWidth, style.font ?? DEFAULT_FONT, style.size ?? DEFAULT_SIZE)) {
            this.ensureSpace(lineHeight);
            this.drawText(line, this.x, this.y, style, boxWidth);
            this.y += lineHeight;
        }
        this.y += marginBottom;
    }

    /**
     * Renders `render` without splitting it across pages: if it overflows, it's redone at the
     * top of a new page. A block taller than a whole page is redone with page breaks allowed.
     */
    keepTogether(render: () => void) {
        if (this.breaksSuppressed) {
            render();
            return;
        }
        const startOps = this.ops.length;
        const startY = this.y;
        this.breaksSuppressed = true;
        try {
            render();
        } finally {
            this.breaksSuppressed = false;
        }
        if (this.y <= this.bottom) return;

        this.ops.length = startOps;
        this.y = startY;
        if (startY > this.margins.top + 0.01) this.addPage();
        render();
    }

    /**
     * Lays columns out side by side, then moves the cursor below the tallest one. The row is kept
     * on one page; if it's taller than a page, every column starts at the same spot and flows onto
     * the following pages independently.
     */
    columns(columns: Column[]) {
        const margins = columns.reduce((sum, c) => sum + (c.marginRight ?? 0), 0);
        const widths = columns.reduce((sum, c) => sum + c.width, 0);
        const scale = widths > 0 ? Math.min(1, Math.max(0, this.width - margins) / widths) : 1;

        this.keepTogether(() => {
            const outer = { x: this.x, width: this.width };
            const start = { page: this.pageIndex, y: this.y };
            let end = { ...start };
            let left = outer.x;
            for (const column of columns) {
                this.pageIndex = start.page;
                this.y = start.y;
                this.x = left;
                this.width = column.width * scale;
                column.render();
                if (this.pageIndex > end.page || (this.pageIndex === end.page && this.y > end.y)) {
                    end = { page: this.pageIndex, y: this.y };
                }
                left += this.width + (column.marginRight ?? 0);
            }
            this.x = outer.x;
            this.width = outer.width;
            this.pageIndex = end.page;
            this.y = end.y;
        });
    }

    /** A table row: cells wrap inside their width, the row is never split across pages. */
    row(cells: RowCell[], options: RowOptions = {}) {
        const { paddingX = 0, paddingY = 0, background, borderBottom } = options;
        const contentHeight = Math.max(0, ...cells.map(c => this.textHeight(c.text, c.width - paddingX * 2, c.style)));
        const border = borderBottom?.width ?? 0;
        const height = contentHeight + paddingY * 2 + border;
        this.ensureSpace(height);

        const width = cells.reduce((sum, c) => sum + c.width, 0);
        if (background !== undefined) this.drawRect(this.x, this.y, width, height, background);
        let left = this.x;
        for (const cell of cells) {
            const innerWidth = cell.width - paddingX * 2;
            const style = cell.style ?? {};
            let top = this.y + paddingY;
            for (const line of wrapText(cell.text, innerWidth, style.font ?? DEFAULT_FONT, style.size ?? DEFAULT_SIZE)) {
                this.drawText(line, left + paddingX, top, style, innerWidth);
                top += this.lineHeight(style);
            }
            left += cell.width;
        }
        if (borderBottom !== undefined) this.drawRect(this.x, this.y + height - border, width, border, borderBottom.color);
        this.y += height;
    }

    /** Draws fixed content (page numbers, footers) on every page once the page count is known. */
    decoratePages(draw: (pageNumber: number, totalPages: number) => void) {
        const current = this.pageIndex;
        for (let i = 0; i < this.pages.length; i++) {
            this.pageIndex = i;
            draw(i + 1, this.pages.length);
        }
        this.pageIndex = current;
    }

    // ── serialisation ──

    toBytes(): Uint8Array {
        const chunks: Uint8Array[] = [];
        let length = 0;
        const offsets: number[] = [];
        const write = (data: string | Uint8Array) => {
            const bytes = typeof data === 'string' ? latin1(data) : data;
            chunks.push(bytes);
            length += bytes.length;
        };
        const object = (id: number, body: string) => {
            offsets[id] = length;
            write(`${id} 0 obj\n${body}\nendobj\n`);
        };
        const stream = (id: number, dict: string, data: Uint8Array) => {
            offsets[id] = length;
            write(`${id} 0 obj\n<< ${dict} /Length ${data.length} >>\nstream\n`);
            write(data);
            write('\nendstream\nendobj\n');
        };

        // 1 catalog, 2 page tree, 3 info, then fonts, images, and a page + content pair per page.
        const fontIds = FONT_NAMES.map((_, i) => 4 + i);
        const imageList = Array.from(this.images.entries());
        const imageIds = imageList.map((_, i) => 4 + FONT_NAMES.length + i);
        const firstPageId = 4 + FONT_NAMES.length + imageList.length;
        const pageIds = this.pages.map((_, i) => firstPageId + i * 2);

        const fonts = FONT_NAMES.map((name, i) => `/F${i + 1} ${fontIds[i]} 0 R`).join(' ');
        const xObjects = imageList.map(([, name], i) => `/${name} ${imageIds[i]} 0 R`).join(' ');
        const resources = `<< /Font << ${fonts} >>${xObjects ? ` /XObject << ${xObjects} >>` : ''} >>`;

        write('%PDF-1.4\n');
        write(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));
        object(1, '<< /Type /Catalog /Pages 2 0 R >>');
        object(2, `<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`);
        object(3, `<< /Producer (Maintener)${this.title ? ` /Title <${utf16Hex(this.title)}>` : ''} >>`);
        FONT_NAMES.forEach((name, i) => object(fontIds[i], `<< /Type /Font /Subtype /Type1 /BaseFont /${name} /Encoding /WinAnsiEncoding >>`));
        imageList.forEach(([image], i) => stream(
            imageIds[i],
            `/Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode`,
            image.bytes,
        ));
        this.pages.forEach((ops, i) => {
            object(pageIds[i], `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${fmt(this.pageWidth)} ${fmt(this.pageHeight)}] /Resources ${resources} /Contents ${pageIds[i] + 1} 0 R >>`);
            stream(pageIds[i] + 1, '', latin1(ops.join('\n')));
        });

        const size = firstPageId + this.pages.length * 2;
        const xrefOffset = length;
        let xref = `xref\n0 ${size}\n0000000000 65535 f \n`;
        for (let id = 1; id < size; id++) xref += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`;
        write(`${xref}trailer\n<< /Size ${size} /Root 1 0 R /Info 3 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`);

        const out = new Uint8Array(length);
        let position = 0;
        for (const chunk of chunks) {
            out.set(chunk, position);
            position += chunk.length;
        }
        return out;
    }

    toBlob(): Blob {
        // toBytes() allocates a fresh, exactly-sized array, so its buffer holds just the PDF bytes.
        return new Blob([this.toBytes().buffer as ArrayBuffer], { type: 'application/pdf' });
    }
}

/** Content streams and dictionaries are ASCII-only (text is hex-encoded), so this is lossless. */
const latin1 = (text: string) => {
    const bytes = new Uint8Array(text.length);
    for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i) & 0xff;
    return bytes;
};

/** Document info strings are UTF-16BE with a BOM, so the title can contain any character. */
const utf16Hex = (text: string) => {
    let hex = 'FEFF';
    for (let i = 0; i < text.length; i++) hex += text.charCodeAt(i).toString(16).padStart(4, '0');
    return hex;
};
